import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,  // Changed: Now using the updated Grid component
  Paper,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  Avatar,
  List,
  ListItem,
  Divider,
  ListItemAvatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack,
  Groups,
  Business,
  Email,
  Phone,
  People,
  DeleteOutline,
  RestoreFromTrash,
  DeleteSweep,
} from '@mui/icons-material';
import axios from 'axios';

import Sidebar from '../components/sidebars/Sidebar';
import TopNav from '../components/top_bars/TopNav';
import StatisticsCards from '../components/top_bars/StatisticsCard';

const Dept = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [inactiveStaff, setInactiveStaff] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [terminationDetails, setTerminationDetails] = useState({
    termination_type: 'Resignation',
    reason: '',
    last_working_day: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    fetchDepartments();
    fetchInactiveStaff();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/dept.php`);
      if (response.data.success) {
        setDepartments(response.data.data.departments);
      } else {
        setError(response.data.message || 'Failed to fetch departments');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffByDepartment = async (dept) => {
    try {
      setLoading(true);
      setSelectedDept(dept);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/dept.php?department=${encodeURIComponent(dept)}&status=Active`
      );
      if (response.data.success) {
        setStaff(response.data.data.staff);
      } else {
        setError(response.data.message || 'Failed to fetch staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchInactiveStaff = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_inactive_staff.php`);
      if (response.data.success) {
        setInactiveStaff(response.data.data.staff);
      }
    } catch (err) {
      console.error('Error fetching inactive staff:', err);
    }
  };

  const handleDeactivateEmployee = async () => {
    try {
      const payload = {
        phone: selectedEmployee.phone,
        termination_type: terminationDetails.termination_type,
        reason: terminationDetails.reason,
        last_working_day: terminationDetails.last_working_day,
        terminated_by: localStorage.getItem('userPhone') || 'HR',
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/deactivate_employee.php`,
        payload
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Employee deactivated successfully',
          severity: 'success',
        });
        
        if (selectedDept) {
          fetchStaffByDepartment(selectedDept);
        } else {
          fetchDepartments();
        }
        fetchInactiveStaff();
        
        setDeleteDialogOpen(false);
        setSelectedEmployee(null);
        setTerminationDetails({
          termination_type: 'Resignation',
          reason: '',
          last_working_day: '',
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error deactivating employee',
        severity: 'error',
      });
    }
  };

  const handleReactivateEmployee = async (employee) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reactivate_employee.php`,
        { phone: employee.phone }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Employee reactivated successfully',
          severity: 'success',
        });
        
        fetchInactiveStaff();
        if (selectedDept) {
          fetchStaffByDepartment(selectedDept);
        } else {
          fetchDepartments();
        }
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error reactivating employee',
        severity: 'error',
      });
    }
  };

  const handleBackToList = () => {
    setSelectedDept(null);
    setStaff([]);
  };

  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const openDeleteDialog = (employee) => {
    setSelectedEmployee(employee);
    setDeleteDialogOpen(true);
  };

  const renderStaffList = (staffList, showActions = true) => {
    if (staffList.length === 0) {
      return (
        <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
          No staff members found
        </Typography>
      );
    }

    return (
      <List disablePadding>
        {staffList.map((person, index) => (
          <React.Fragment key={person.id}>
            <ListItem sx={{ py: 2 }} alignItems="flex-start">
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: stringToColor(person.surname),
                    width: 56,
                    height: 56,
                    mr: 2,
                  }}
                >
                  {person.surname?.charAt(0)}
                  {person.other_names?.charAt(0)}
                </Avatar>
              </ListItemAvatar>
              
              <div style={{ flex: 1 }}>
                <Typography variant="h6" component="div" gutterBottom>
                  {person.surname} {person.other_names}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" component="div">
                  <Email fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {person.email}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" component="div" sx={{ mt: 0.5 }}>
                  <Phone fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {person.phone}
                </Typography>
                
                {person.termination_type && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`Terminated: ${person.termination_type}`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </Box>
                )}
              </div>
              
              {showActions && (
                <Box>
                  <IconButton
                    color="error"
                    onClick={() => openDeleteDialog(person)}
                    title="Remove Employee"
                    size="large"
                  >
                    <DeleteOutline />
                  </IconButton>
                </Box>
              )}
              
              {!showActions && (
                <Box>
                  <IconButton
                    color="primary"
                    onClick={() => handleReactivateEmployee(person)}
                    title="Reactivate Employee"
                    size="large"
                  >
                    <RestoreFromTrash />
                  </IconButton>
                </Box>
              )}
            </ListItem>
            {index < staffList.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
          overflow: 'auto',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 2 }}>
          <StatisticsCards />
        </Box>

        {/* Updated Grid container with new v2 syntax */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid size={12}>  {/* Changed: size prop instead of xs */}
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 4,
                width: '100%',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              {loading && !selectedDept ? (
                <Box display="flex" justifyContent="center" minHeight="40vh" alignItems="center">
                  <CircularProgress size={60} />
                </Box>
              ) : error ? (
                <Typography color="error" variant="h6">
                  {error}
                </Typography>
              ) : !selectedDept ? (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center">
                      <Groups color="primary" sx={{ fontSize: 40, mr: 2 }} />
                      <Typography variant="h5" component="h2">
                        Departments
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteSweep />}
                      onClick={() => setTabValue(1)}
                    >
                      View Inactive Staff ({inactiveStaff.length})
                    </Button>
                  </Box>

                  <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
                    <Tab label="Active Staff" />
                    <Tab label="Inactive Staff" />
                  </Tabs>

                  {tabValue === 0 ? (
                    departments.length === 0 ? (
                      <Typography>No departments found</Typography>
                    ) : (
                      // Updated Grid container with new v2 syntax for department cards
                      <Grid container spacing={3}>
                        {departments.map((dept) => (
                          <Grid key={dept.department} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            {/* size prop with object for responsive values */}
                            <Paper
                              elevation={2}
                              sx={{
                                p: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: 4,
                                  backgroundColor: 'action.hover',
                                },
                              }}
                              onClick={() => fetchStaffByDepartment(dept.department)}
                            >
                              <Business color="primary" sx={{ fontSize: 40, mb: 1 }} />
                              <Typography variant="h6" align="center">
                                {dept.department}
                              </Typography>
                              <Chip
                                label={`${dept.staff_count} ${dept.staff_count === 1 ? 'member' : 'Staff'}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1.5 }}
                                icon={<People fontSize="small" />}
                              />
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )
                  ) : (
                    renderStaffList(inactiveStaff, false)
                  )}
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleBackToList}
                    sx={{ mb: 3 }}
                    startIcon={<ArrowBack />}
                  >
                    All Departments
                  </Button>

                  <Box display="flex" alignItems="center" mb={3}>
                    <Business color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">{selectedDept} Staff</Typography>
                  </Box>

                  {loading ? (
                    <Box display="flex" justifyContent="center" minHeight="40vh" alignItems="center">
                      <CircularProgress size={60} />
                    </Box>
                  ) : (
                    renderStaffList(staff, true)
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog - unchanged */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Employee</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to remove <strong>{selectedEmployee?.surname} {selectedEmployee?.other_names}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
            This action will mark the employee as inactive and they will be moved to the inactive staff list.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Termination Type</InputLabel>
            <Select
              value={terminationDetails.termination_type}
              onChange={(e) => setTerminationDetails({ ...terminationDetails, termination_type: e.target.value })}
              label="Termination Type"
            >
              <MenuItem value="Resignation">Resignation</MenuItem>
              <MenuItem value="Termination">Termination</MenuItem>
              <MenuItem value="Retirement">Retirement</MenuItem>
              <MenuItem value="End of Contract">End of Contract</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Reason for Leaving"
            multiline
            rows={3}
            value={terminationDetails.reason}
            onChange={(e) => setTerminationDetails({ ...terminationDetails, reason: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            type="date"
            label="Last Working Day"
            value={terminationDetails.last_working_day}
            onChange={(e) => setTerminationDetails({ ...terminationDetails, last_working_day: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeactivateEmployee} variant="contained" color="error">
            Confirm Removal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications - unchanged */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dept;